import React, { useState } from "react";
import Navbar from "../components/Navbar";
import { FiChevronDown } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import { Link } from "react-router-dom";
import NewsletterForm from "../components/NewsletterForm";

// Single service card with expandable details
const ServiceCard = ({ title, img, summary, full }) => {
  const [expanded, setExpanded] = useState(false);
  const panelId = `${title.replace(/\s+/g, "-").toLowerCase()}-panel`;

  return (
    <article className="relative bg-white rounded-2xl shadow-md p-6 pt-8 hover:shadow-lg transition">
      {/* Image at top (replace src with your real image) */}
      <div className="w-full h-40 rounded-xl overflow-hidden bg-brand-50 border border-brand-200">
        {img ? (
          <img src={img} alt={title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full grid place-items-center text-brand-600 text-sm">Image Placeholder</div>
        )}
      </div>

      <h3 className="text-lg font-semibold text-brand-900 mt-4 text-center">{title}</h3>

      {/* Short text under image */}
      <p className="text-gray-600 text-sm mt-2 text-center">{summary}</p>

      {/* Toggle button */}
      <div className="mt-4 grid place-items-center">
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={panelId}
          onClick={() => setExpanded((v) => !v)}
          className="w-10 h-10 rounded-full bg-brand-100 text-brand-700 grid place-items-center hover:bg-brand-200 transition"
          title={expanded ? "Hide details" : "Show details"}
        >
          <FiChevronDown className={`transition-transform duration-300 ${expanded ? "rotate-180" : "rotate-0"}`} />
        </button>
      </div>

      {/* Expandable content inside same card */}
      <div
        id={panelId}
        className={`overflow-hidden transition-all duration-300 ${expanded ? "max-h-[500px] opacity-100 mt-3" : "max-h-0 opacity-0"}`}
      >
        <div className="text-sm text-gray-700 leading-relaxed">
          {full}
        </div>
      </div>
    </article>
  );
};

const Service = () => {
  const services = [
    {
      title: "CCTV Installation",
      img: "/images/cctv_installation.png",
      summary: "High-definition surveillance systems for 24/7 monitoring of your home or business premises.",
      full:
        "We provide professional CCTV installation services using the latest high-definition cameras and recording equipment. Our systems offer remote viewing capabilities, motion detection, and night vision, ensuring your property is monitored and protected around the clock.",
    },
    {
      title: "Electric Fencing",
      img: "/images/electric_fence.png",
      summary: "Advanced perimeter security solutions to deter intruders and protect your property boundaries.",
      full:
        "We design and install high-voltage electric fencing systems that serve as both a physical barrier and a powerful deterrent. Our installations include alarm integration, backup power systems, and comply with all safety regulations to provide maximum security.",
    },
    {
      title: "Gate Automation",
      img: "/images/gate_automation.png",
      summary: "Seamless and secure automated gate systems for convenient access control and enhanced privacy.",
      full:
        "Upgrade your entrance with our reliable gate automation services. We install heavy-duty motors for sliding and swinging gates, integrated with remote controls, keypads, and intercoms, providing you with effortless and secure access to your premises.",
    },
    {
      title: "Alarm Systems",
      img: "/images/alarm.png",
      summary: "Intelligent intrusion detection systems to protect your property from unauthorized entry.",
      full:
          "Our advanced alarm systems provide comprehensive protection for homes and businesses. We install motion sensors, door/window contacts, and vibration sensors that trigger immediate sirens and mobile notifications. Our systems can be monitored 24/7, ensuring a rapid response to any security breach.",
    },
    {
      title: "Fire Alarm Systems",
      img: "/images/alarm_system.png",
      summary: "Advanced fire detection and alarm systems to safeguard lives and property from fire hazards.",
      full:
          "Protect your premises with our state-of-the-art fire alarm systems. We provide comprehensive installation of smoke detectors, heat sensors, and manual call points, all integrated into a central control panel. Our systems ensure early detection and rapid response to any fire emergency, complying with the highest safety standards.",
    },
    {
      title: "Access Control Installation",
      img: "/images/access_control.png",
      summary: "Sophisticated entry management systems to regulate and monitor access to sensitive areas.",
      full:
          "Our access control solutions range from simple keypad entry to advanced biometric and RFID card systems. We help you manage who enters your building and when, providing detailed audit trails and enhancing the overall security of your facility.",
    },
    {
      title: "Intercom Video Monitoring",
      img: "/images/intercom.png",
      summary: "Integrated video intercom systems for secure communication and visitor verification.",
      full:
          "See and speak with visitors before granting access with our high-quality video intercom systems. These units can be integrated with your mobile devices and gate automation, allowing you to manage your entrance from anywhere with complete peace of mind.",
    },
    {
      title: "Satellite and Dish Installation",
      img: "/images/satellite.png",
      summary: "Expert setup of satellite and digital TV systems for crystal-clear reception and global connectivity.",
      full:
        "Our technicians specialize in the precise installation and alignment of satellite dishes and digital TV systems. Whether for residential entertainment or commercial communication needs, we ensure optimal signal strength and a wide range of channel access.",
    },


    {
      title: "Skills Training",
      img: "/images/training.png",
      summary: "Professional technical training in security system installation, maintenance, and operation.",
      full:
        "We offer comprehensive training programs for individuals looking to gain expertise in security technology. Our courses cover CCTV setup, electric fence installation, and system troubleshooting, empowering you with the skills needed for a career in the security industry.",
    },
  ];

  return (
    <main className="pt-24 text-brand-800 bg-white min-h-screen route-enter">
      <Navbar />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="w-full h-full bg-gradient-to-b from-brand-900 to-brand-700" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 py-16 md:py-20 grid md:grid-cols-2 gap-8 items-center text-white">
          <div>
            {/*<p className="uppercase tracking-widest text-brand-200 text-xs mb-2">Gesingo Security Technology </p>*/}
            <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-4">Services We Offer</h1>
            <p className="text-brand-100 max-w-xl">
              We are a premier security technology provider, delivering cutting-edge surveillance, access control, and 
              integrated security solutions to protect what matters most to you.
            </p>
          </div>
          {/* Hero image placeholder */}
          <div className="relative">
            <div className="rounded-[2rem] border-4 border-brand-accent bg-white/5 h-64 md:h-80 w-full overflow-hidden flex items-center justify-center">
              <img
                  src="/images/service.png"
                  alt="Contact Gesingo Security Technology"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Intro heading */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <p className="text-center text-sm text-brand-600 font-semibold mb-2">Features Services</p>
        <h2 className="text-center text-3xl md:text-4xl font-extrabold text-brand-950 leading-snug">
          A wide range of
          <br className="hidden md:block" /> services
        </h2>
      </section>

      {/* Services grid */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s) => (
            <ServiceCard key={s.title} {...s} />
          ))}
        </div>
      </section>

      {/* Newsletter (same footer sections as Contact.jsx) */}
      <section className="bg-gradient-to-br from-brand-900 to-brand-800 text-white">
        <div className="max-w-6xl mx-auto px-6 py-12 grid md:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <h3 className="text-2xl font-semibold mb-1">Our Newsletters</h3>
            <p className="text-brand-100 max-w-xl">Subscribe to receive updates about our services and initiatives.</p>
          </div>
          <NewsletterForm />
        </div>
      </section>

      <section className="bg-brand-50">
        <div className="max-w-6xl mx-auto px-6 py-12 grid md:grid-cols-4 gap-8 bg-white rounded-2xl shadow-sm">
          <div className="space-y-3">
            <img src="/images/logo.png" alt="Gesingo Security Technologylogo" className="h-12 w-12 object-contain rounded" />
              <p className="text-gray-600 text-sm">
                  Providing advanced security technology solutions and expert technical services to safeguard your environment.
              </p>
          </div>
          <div className="col-span-3 grid sm:grid-cols-2 gap-6 text-sm text-gray-700">
            <div>
              <div className="font-semibold mb-2">Navigation</div>
              <ul className="space-y-2">
                <li><Link to="/" className="hover:underline">Home</Link></li>
                <li><Link to="/about" className="hover:underline">About</Link></li>
                <li><Link to="/services" className="hover:underline">Services</Link></li>
                <li><Link to="/contact" className="hover:underline">Contact</Link></li>
              </ul>
            </div>
            <div>
              <div className="font-semibold mb-2">Work Hours</div>
              <ul className="space-y-2">
                <li>7:30AM – 5:30PM, Mon–Sun</li>
                <li className="text-gray-500">Closed on Fridays</li>
              </ul>
              <div className="mt-4 flex items-center gap-3">
                <a href="tel:+233244844226">
                  <button type="button" className="inline-flex px-4 py-2 rounded-full bg-brand-600 text-white hover:bg-brand-700">Call Us</button>
                </a>
                <a
                  href="https://wa.me/233504869983"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="WhatsApp"
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-500 text-white hover:bg-brand-600 shadow"
                >
                  <FaWhatsapp className="text-lg" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="text-center text-gray-500 text-sm py-6">
        <p>© {new Date().getFullYear()} Gesingo Security Technology</p>
        <p className="mt-1">Designed by <a href="https://wa.me/233545038856" className="text-brand-600 hover:underline" target="_blank" rel="noopener noreferrer">WASS TECH SOLUTIONS</a></p>
      </footer>
    </main>
  );
};

export default Service;
